import React, { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { Loader2, XCircle, CheckCircle, Clock } from "lucide-react";
import { MdDocumentScanner } from "react-icons/md";
import Loader from "../widgets/Loader";
import {
  dashboardService,
  WorkflowStatusData,
} from "../services/dashboard.service";
import { LuFiles } from "react-icons/lu";
import WorkflowStatusCard from "../components/WorkflowStatusCard";
import WorkflowExecutionChart from "../components/WorkflowExecutionChart";
import WorkflowTemplateStatus from "../components/WorkflowTemplateStatus";

interface ProcessingData {
  name: string;
  overall_processing: {
    start_time: string;
    end_time: string;
    time_taken: number;
    status: string;
    error?: { name: string };
  };
}

interface ChartData {
  date: string;
  success: number;
  failed: number;
  total: number;
}

interface DocumentType {
  file_type: string;
  total_count: number;
}

interface DocumentMetrics {
  document_store_count: number;
  files_count: number;
  processed_today: number;
  processing_queue: number;
  document_type: DocumentType[];
}

// const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#f59e0b", "#8b5cf6", "#a3a3a3"];
const COLORS = [
  "#f59e0b", // Orange (PNG)
  "#00C49F", // Teal (PDF)
  "#3b82f6", // Blue (CSV)
  "#f43f5e", // Red (Word Doc)
  "#8b5cf6", // Purple (for what was JPEG)
  "#a3a3a3", // Indigo (TXT)
  "#22C55E", // Green (Excel)
  "#ff7300", 
  "#38bdf8",
  "#e879f9",
  "#fbbf24",
];

const RADIAN = Math.PI / 180;
const renderCustomizedLabel = ({
  cx,
  cy,
  midAngle,
  outerRadius,
  name,
  percent,
}: any) => {
  // Add more dynamic spacing so labels have room
  const radius = outerRadius + 35;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);

  // Return standard percentage
  const percentText = `${(percent * 100).toFixed(0)}%`;

  return (
    <text
      x={x}
      y={y}
      fill="#374151"
      textAnchor={x > cx ? "start" : "end"}
      dominantBaseline="central"
      fontSize={12}
      className="font-medium"
    >
      {`${shortenFileType(name)} ${percentText}`}
    </text>
  );
};

const shortenFileType = (fileType: string) => {
  // If it's already a short string, just return it
  if (!fileType.includes("/")) return fileType.toUpperCase();

  const map: Record<string, string> = {
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "Word Doc",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation": "PPTX",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "XLSX",
    "application/msword": "Word Doc",
    "application/vnd.ms-excel": "XLSX",
    "application/vnd.ms-powerpoint": "PPTX",
    "text/plain": "TXT",
    "image/png": "PNG",
    "image/jpeg": "JPEG",
    "image/jpg": "JPEG",
    "application/pdf": "PDF",
    "text/csv": "CSV",
    "video/mp4": "MP4",
  };

  if (map[fileType]) return map[fileType];

  let shortened = fileType.split("/").pop()?.toUpperCase() || fileType;
  if (shortened.includes("PRESENTATIONML")) return "PPTX";
  if (shortened.includes("SPREADSHEETML")) return "XLSX";
  if (shortened.includes("WORDPROCESSINGML")) return "Word Doc";
  if (shortened.length > 20) return shortened.substring(0, 17) + "...";
  return shortened;
};

const Dashboard: React.FC = () => {
  const [processingData, setProcessingData] = useState<ChartData[]>([]);
  const [metrics, setMetrics] = useState<DocumentMetrics | null>(null);
  const [workflowStatus, setWorkflowStatus] =
    useState<WorkflowStatusData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingDataLoading, setProcessingDataLoading] = useState(false);
  const [processingDataError, setProcessingDataError] = useState<string | null>(
    null
  );
  const [startDate, setStartDate] = useState(
    new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0]
  );
  const [endDate, setEndDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  useEffect(() => {
    const initialFetch = async () => {
      setLoading(true);
      setError(null);
      await Promise.all([
        fetchMetrics(),
        fetchProcessingData(true),
        fetchWorkflowStatus(),
      ]);
      setLoading(false);
    };
    initialFetch();
  }, []);

  const normalizeDocumentTypes = (data: any[]) => {
    const converted: Record<string, number> = {};

    data.forEach((item) => {
      // ✅ Custom conversions / Groupings directly using shortenFileType
      let type = shortenFileType(item.file_type);

      // ✅ Aggregate counts
      if (converted[type]) {
        converted[type] += item.total_count;
      } else {
        converted[type] = item.total_count;
      }
    });

    // ✅ Return as array for your chart
    return Object.entries(converted).map(([file_type, total_count]) => ({
      file_type,
      total_count,
    }));
  };

  const fetchMetrics = async () => {
    try {
      const result = await dashboardService.getMetrics();
      if (result.success && result.data) {
        const normalizedDocumentType = normalizeDocumentTypes(
          result.data.document_type
        );
        setMetrics({
          document_store_count: result.data.document_store_count,
          files_count: result.data.files_count,
          processed_today: result.data.processed_today,
          processing_queue: result.data.processing_queue,
          document_type: normalizedDocumentType || [],
        });
      } else {
        setError("Failed to fetch metrics data");
      }
    } catch (err) {
      setError("Error fetching metrics: " + (err as Error).message);
    }
  };

  const fetchWorkflowStatus = async () => {
    try {
      const result = await dashboardService.getWorkflowStatus();
      if (result.success && result.data) {
        setWorkflowStatus(result.data);
      } else {
        console.error("Failed to fetch workflow status data");
      }
    } catch (err) {
      console.error(
        "Error fetching workflow status: " + (err as Error).message
      );
    }
  };

  const fetchProcessingData = async (isInitialLoad = false) => {
    try {
      if (isInitialLoad) {
        setLoading(true);
        setError(null);
      } else {
        setProcessingDataLoading(true);
        setProcessingDataError(null);
      }

      const result = await dashboardService.getProcessingVolume(
        startDate,
        endDate
      );

      if (result.success && result.data) processProcessingData(result.data);
      else {
        if (isInitialLoad) setError("Failed to fetch processing data");
        else setProcessingDataError("Failed to fetch processing data");
      }
    } catch (err) {
      if (isInitialLoad)
        setError("Error fetching data: " + (err as Error).message);
      else
        setProcessingDataError(
          "Error fetching data: " + (err as Error).message
        );
    } finally {
      if (isInitialLoad) setLoading(false);
      else setProcessingDataLoading(false);
    }
  };

  const processProcessingData = (rawData: ProcessingData[]) => {
    const dateMap = new Map<
      string,
      { success: number; failed: number; total: number }
    >();
    rawData.forEach((item) => {
      const date = new Date(item.overall_processing.start_time);
      const formattedDate = `${date.getMonth() + 1}/${date.getDate()}`;
      if (!dateMap.has(formattedDate))
        dateMap.set(formattedDate, { success: 0, failed: 0, total: 0 });
      const dayData = dateMap.get(formattedDate)!;
      dayData.total += 1;
      if (item.overall_processing.status === "DONE") dayData.success += 1;
      else if (item.overall_processing.status === "FAILED") dayData.failed += 1;
    });

    setProcessingData(
      Array.from(dateMap.entries())
        .map(([date, counts]) => ({ date, ...counts }))
        .sort((a, b) => {
          const [aM, aD] = a.date.split("/").map(Number);
          const [bM, bD] = b.date.split("/").map(Number);
          return aM !== bM ? aM - bM : aD - bD;
        })
    );
  };

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload || !payload.length) return null;
    const { date, total, success, failed } = payload[0].payload;
    return (
      <div className="bg-gray-900 text-white p-3 rounded-lg shadow-lg text-sm">
        <p className="font-bold mb-1">{date}</p>
        <p>Total: {total}</p>
        <p className="text-green-400">Success: {success}</p>
        <p className="text-red-400">Failed: {failed}</p>
      </div>
    );
  };

  if (loading)
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <Loader />
      </div>
    );

  if (error)
    return (
      <div className="flex items-center justify-center h-screen bg-red-50">
        <div className="bg-white p-6 rounded-xl shadow-lg text-center">
          <XCircle className="w-12 h-12 mx-auto text-red-500 mb-2" />
          <p className="text-red-600 font-semibold">{error}</p>
        </div>
      </div>
    );

  return (
    <div className="min-h-screen bg-gray-50 space-y-6">
      <div className="space-y-6">
        {/* First Row - 4 Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          {metrics && (
            <>
              <div className="bg-white p-6 rounded-md transition flex items-center border border-gray-200">
                <MdDocumentScanner className="w-10 h-10 text-indigo-500 mr-4" />
                <div>
                  <p className="text-indigo-600 font-semibold text-sm">
                    Document Store Count
                  </p>
                  <p className="text-2xl font-bold text-gray-800">
                    {metrics.document_store_count}
                  </p>
                </div>
              </div>

              <div className="bg-white p-6 rounded-md transition flex items-center border border-gray-200">
                <LuFiles className="w-10 h-10 text-green-500 mr-4" />
                <div>
                  <p className="text-green-600 font-semibold text-sm">
                    Files Count
                  </p>
                  <p className="text-2xl font-bold text-gray-800">
                    {metrics.files_count}
                  </p>
                </div>
              </div>

              <div className="bg-white p-6 rounded-md transition flex items-center border border-gray-200">
                <CheckCircle className="w-10 h-10 text-blue-500 mr-4" />
                <div>
                  <p className="text-blue-600 font-semibold text-sm">
                    Processed Today
                  </p>
                  <p className="text-2xl font-bold text-gray-800">
                    {metrics.processed_today}
                  </p>
                </div>
              </div>

              <div className="bg-white p-6 rounded-md transition flex items-center border border-gray-200">
                <Clock className="w-10 h-10 text-red-500 mr-4" />
                <div>
                  <p className="text-red-600 font-semibold text-sm">
                    Processing Queue
                  </p>
                  <p className="text-2xl font-bold text-gray-800">
                    {metrics.processing_queue}
                  </p>
                </div>
              </div>
            </>
          )}
        </div>

        {/* Second Row - 70% / 30% */}
        <div className="grid grid-cols-1 lg:grid-cols-10 gap-6">
          {/* Left - Processing Volume Trends */}
          <div className="lg:col-span-6 bg-white p-4 rounded-md border border-gray-200 relative">
            {/* Small Date Picker on top-right */}
            <div className="absolute top-4 right-4 flex items-center gap-2 text-gray-600">
              <input
                type="date"
                value={startDate}
                max={endDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              />
              <input
                type="date"
                value={endDate}
                min={startDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              />
              <button
                onClick={() => fetchProcessingData()}
                className="bg-blue-600 text-white px-3 py-1 rounded text-sm hover:bg-blue-700 transition"
              >
                Go
              </button>
            </div>

            <h2 className="text-xl font-bold text-gray-800 mb-4">
              Processing Volume Trends
            </h2>
            {processingDataLoading ? (
              <div className="flex items-center justify-center h-96">
                <Loader2 className="w-10 h-10 animate-spin text-blue-600" />
              </div>
            ) : processingDataError ? (
              <div className="flex items-center justify-center h-96 bg-red-50">
                <div className="bg-white p-6 rounded-xl shadow-lg text-center">
                  <XCircle className="w-10 h-10 mx-auto text-red-500 mb-2" />
                  <p className="text-red-600 font-semibold">
                    {processingDataError}
                  </p>
                </div>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={400}>
                <BarChart
                  data={processingData}
                  margin={{ top: 20, right: 20, left: 0, bottom: 20 }}
                >
                  <defs>
                    <linearGradient
                      id="successGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor="#10b981" stopOpacity={0.9} />
                      <stop
                        offset="100%"
                        stopColor="#059669"
                        stopOpacity={0.9}
                      />
                    </linearGradient>
                    <linearGradient
                      id="failedGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor="#ef4444" stopOpacity={0.9} />
                      <stop
                        offset="100%"
                        stopColor="#dc2626"
                        stopOpacity={0.9}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#e5e7eb"
                    strokeOpacity={0.5}
                  />
                  <XAxis
                    dataKey="date"
                    stroke="#6b7280"
                    style={{ fontSize: "12px" }}
                    tickLine={false}
                  />
                  <YAxis
                    stroke="#6b7280"
                    style={{ fontSize: "12px" }}
                    tickLine={false}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    cursor={{ fill: "rgba(59,130,246,0.1)" }}
                  />
                  <Legend
                    wrapperStyle={{ paddingTop: "10px" }}
                    iconType="circle"
                  />
                  <Bar
                    dataKey="success"
                    stackId="a"
                    fill="url(#successGradient)"
                    name="Success"
                    maxBarSize={50}
                  />
                  <Bar
                    dataKey="failed"
                    stackId="a"
                    fill="url(#failedGradient)"
                    name="Failed"
                    maxBarSize={50}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>

          {/* Right - Document Type Pie Chart */}
          <div className="lg:col-span-4 bg-white p-6 rounded-md border border-gray-200">
            <h3 className="text-xl font-bold text-gray-800 mb-4">
              Document Type Distribution
            </h3>
            {metrics && metrics.document_type.length > 0 && (
              <ResponsiveContainer width="100%" height={400}>
                {/* Key Change 1: Added margin to create space for labels */}
                <PieChart margin={{ top: 20, right: 30, left: 30, bottom: 20 }}>
                  <Pie
                    data={metrics.document_type as any}
                    dataKey="total_count"
                    nameKey="file_type"
                    cx="50%"
                    cy="50%"
                    innerRadius="45%"  // Creates a donut chart look for better label spreading
                    outerRadius="65%"  // Makes the chart body bigger to fill empty space
                    fill="#3b82f6"
                    paddingAngle={2}   // Adds a nice visual separation between slices
                    minAngle={15}      // Forces tiny slices to take up enough visual space so labels don't bunch up
                    labelLine={{ stroke: "#9ca3af", strokeWidth: 1 }}
                    label={renderCustomizedLabel}
                  >
                    {metrics.document_type.map((_, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    content={({ active, payload }) =>
                      active && payload && payload.length ? (
                        <div className="bg-white p-3 rounded-xl shadow-md border border-gray-100 text-sm">
                          <p className="font-semibold text-gray-700">
                            {/* Use the helper function in the tooltip too! */}
                            {shortenFileType(payload[0].name)}
                          </p>
                          <p className="text-gray-500">
                            Count: {payload[0].value}
                          </p>
                        </div>
                      ) : null
                    }
                  />
                  <Legend
                    // Key Change 4: The formatter uses our required helper function
                    formatter={(value) => shortenFileType(value)}
                    verticalAlign="bottom"
                    iconSize={14}
                    wrapperStyle={{ paddingTop: "20px" }}
                  />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        {/* Third Row - Workflow Status Card */}
        {workflowStatus && (
          <div className="mt-6">
            <WorkflowStatusCard
              totalWorkflow={workflowStatus.total_Workflow}
              processDone={workflowStatus.overall_process_done}
              processFail={workflowStatus.overall_process_fail}
              processInProgress={workflowStatus.overall_process_inprogress}
            />
          </div>
        )}

        {/* Fifth Row - Workflow Execution Chart */}
        <div className="mt-6">
          <WorkflowExecutionChart />
        </div>

        {/* Fourth Row - Workflow Template Status */}
        <div className="mt-6">
          <WorkflowTemplateStatus />
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
